import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PolicyStatusBadge } from "./policies/PolicyBadges";
import { PolicyPreview } from "./policies/PolicyPreview";
import { PolicyVersions, downloadVersion } from "./policies/PolicyVersions";
import { PolicyWorkflowActions } from "./policies/PolicyWorkflowActions";
import { PolicyMetadataCard } from "./policies/PolicyMetadataCard";
import { PolicyExtractionPanel } from "./policies/PolicyExtractionPanel";
import { PolicyAuditTrail } from "./policies/PolicyAuditTrail";

export default function PolicyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/policies/${id}`);
      setDetail(data);
      setSelected((prev) => data.versions.find((v) => v.id === (prev?.id || data.policy.readable_version_id)) || data.versions[0] || null);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setMissing(true);
      else toast.error(err?.response?.data?.detail || "Could not load policy");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/policies/options"), api.get("/policies/categories")])
      .then(([o, c]) => { setOptions(o.data); setCategories(c.data.categories); }).catch(() => {});
  }, []);

  if (missing) {
    return (
      <div className="nv-card p-12 text-center" data-testid="policy-not-found">
        <p className="text-sm font-medium text-slate-700">This policy is not available to you.</p>
        <button onClick={() => navigate("/policies")} className="mt-4 text-sm text-sky-700 underline" data-testid="policy-back-link">Back to Policy Library</button>
      </div>
    );
  }
  if (!detail || !options) return <div className="p-8 text-sm text-slate-500 animate-pulse">Loading policy…</div>;

  const { policy, versions, events, can } = detail;
  const readable = versions.find((v) => v.id === policy.readable_version_id);

  return (
    <div className="space-y-5" data-testid="policy-detail-page">
      <button onClick={() => navigate("/policies")} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="policy-back-button"><ArrowLeft size={15} /> Policy Library</button>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-h2" data-testid="policy-title">{policy.title}</h1>
            <PolicyStatusBadge status={policy.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {policy.category} · {policy.version_label}
            {policy.active_version_id && policy.current_version_id !== policy.active_version_id && <span className="text-amber-700"> · newer version awaiting approval</span>}
          </p>
        </div>
        {can.download && readable && (
          <button onClick={() => downloadVersion(policy.id, readable)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-700 text-white text-sm hover:bg-sky-800 self-start" data-testid="policy-download-button">
            <Download size={15} /> Download {readable.version_label}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5">
        <div className="space-y-5 min-w-0">
          <PolicyPreview policyId={policy.id} versionId={selected?.id} fileType={selected?.file_type} />
          <PolicyMetadataCard policy={policy} can={can} options={options} categories={categories} user={user} onChanged={load} />
          {can.view_text && <PolicyExtractionPanel policyId={policy.id} version={selected} canEdit={can.edit} onChanged={load} />}
        </div>
        <div className="space-y-5">
          <PolicyWorkflowActions policy={policy} can={can} onChanged={load} />
          <PolicyVersions policyId={policy.id} versions={versions} selectedId={selected?.id} onSelect={setSelected} can={can} onChanged={load} policyStatus={policy.status} />
          {can.view_text && <PolicyAuditTrail events={events} />}
        </div>
      </div>
    </div>
  );
}
